import { useEffect, useState } from "react";
import { Package, ShoppingCart, Users, IndianRupee, Clock } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";

const cards = [
  { key: "revenue", label: "Revenue", icon: IndianRupee, format: (v) => `₹${Number(v).toLocaleString("en-IN")}`, color: "amber" },
  { key: "total_orders", label: "Orders", icon: ShoppingCart, format: (v) => v, color: "emerald" },
  { key: "total_products", label: "Products", icon: Package, format: (v) => v, color: "sky" },
  { key: "total_customers", label: "Customers", icon: Users, format: (v) => v, color: "rose" },
];

export default function AdminOverview() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/stats");
        setStats(data);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "Failed to load stats");
      }
    })();
  }, []);

  return (
    <div data-testid="admin-overview">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="overline text-amber-400">Overview</div>
          <h1 className="font-serif-display text-3xl mt-2">Dashboard</h1>
        </div>
        <div className="text-xs font-mono-accent text-slate-500">
          {new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" })}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <div key={c.key}
               className="p-5 rounded-lg bg-slate-800/60 border border-white/5"
               data-testid={`stat-${c.key}`}>
            <div className="flex items-center justify-between">
              <c.icon className={`h-5 w-5 text-${c.color}-400`} />
              {c.key === "total_orders" && stats?.pending_orders > 0 && (
                <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/30">
                  {stats.pending_orders} pending
                </Badge>
              )}
            </div>
            <div className="mt-3 font-serif-display text-2xl lg:text-3xl">
              {stats ? c.format(stats[c.key]) : "—"}
            </div>
            <div className="overline mt-1 text-slate-400">{c.label}</div>
          </div>
        ))}
      </div>

      <div className="mt-8 bg-slate-800/60 border border-white/5 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-serif-display text-xl">Recent orders</h2>
          <Clock className="h-4 w-4 text-slate-500" />
        </div>
        {stats && stats.recent_orders.length > 0 ? (
          <div className="divide-y divide-white/5" data-testid="recent-orders">
            {stats.recent_orders.map((o) => (
              <div key={o.id} className="py-3 flex items-center justify-between text-sm">
                <div>
                  <div className="font-medium">{o.customer_name}</div>
                  <div className="text-xs text-slate-500 font-mono-accent">#{String(o.id).slice(-8)}</div>
                </div>
                <div className="text-right">
                  <div className="font-mono-accent">₹{Number(o.total).toLocaleString("en-IN")}</div>
                  <div className="text-xs text-slate-500 capitalize">{o.status}</div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-sm text-slate-500 py-6 text-center" data-testid="recent-orders-empty">
            No orders yet.
          </div>
        )}
      </div>
    </div>
  );
}
